<template>
  <div class="portal">
    <!-- Header Component -->
    <HeaderComponent />

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-text">
          <span class="hero-badge">Official Government Portal</span>
          <h2 class="hero-title">Pay County Fees<br/><span class="highlight">In Minutes</span></h2>
          <p class="hero-subtitle">
            Access all county services from one secure platform. Pay for permits, parking,
            land rates, and more — anytime, anywhere.
          </p>
          <div class="hero-stats">
            <div class="stat">
              <span class="stat-number">24/7</span>
              <span class="stat-label">Available</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-number">100%</span>
              <span class="stat-label">Secure</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-number">2min</span>
              <span class="stat-label">Average</span>
            </div>
          </div>
        </div>
        <div class="hero-visual">
          <img
              :src="county.handPhone || '/img/phone-mockup.png'"
              alt="Mobile payment"
              class="hero-phone"
          />
        </div>
      </div>
      <div class="hero-wave">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0,64 C480,150 960,-20 1440,64 L1440,120 L0,120 Z" fill="#f8fafc"/>
        </svg>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" class="services-section">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">Our Services</span>
          <h2 class="section-title">What would you like to pay for?</h2>
          <p class="section-subtitle">Select a service below to get started</p>
        </div>

        <div class="services-grid">
          <!-- Business Permit -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'business' }"
              @click="selectService('business')"
          >
            <div class="service-icon business">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
            </div>
            <h3 class="service-title">Business Permit</h3>
            <p class="service-desc">Register new business or renew your Single Business Permit</p>
            <div class="service-actions" v-if="activeService === 'business'">
              <button class="action-btn" @click.stop="goTo('create-business')">
                <span>Register New</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
              <button class="action-btn" @click.stop="goTo('print-permit')">
                <span>Print Permit</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Land Rates -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'land' }"
              @click="selectService('land')"
          >
            <div class="service-icon land">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9,22 9,12 15,12 15,22"/>
              </svg>
            </div>
            <h3 class="service-title">Land Rates</h3>
            <p class="service-desc">Pay your land rates and property taxes</p>
            <div class="service-actions" v-if="activeService === 'land'">
              <button class="action-btn" @click.stop="goTo('land-rate')">
                <span>Pay Land Rate</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Rents -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'rents' }"
              @click="selectService('rents')"
          >
            <div class="service-icon rents">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
              </svg>
            </div>
            <h3 class="service-title">Rents</h3>
            <p class="service-desc">Pay for county housing and market stall rentals</p>
            <div class="service-actions" v-if="activeService === 'rents'">
              <button class="action-btn" @click.stop="goTo('house-rents')">
                <span>House Rent</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
              <button class="action-btn" @click.stop="goTo('market-stalls')">
                <span>Stall Rent</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Parking -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'parking' }"
              @click="selectService('parking')"
          >
            <div class="service-icon parking">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M9 17V7h4a3 3 0 0 1 0 6H9"/>
              </svg>
            </div>
            <h3 class="service-title">Parking Fees</h3>
            <p class="service-desc">Daily parking, seasonal passes, and penalty payments</p>
            <div class="service-actions" v-if="activeService === 'parking'">
              <button class="action-btn" @click.stop="goTo('daily-parking')">
                <span>Daily Parking</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
              <button class="action-btn" @click.stop="goTo('seasonal-parking')">
                <span>Seasonal Pass</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- County Bills -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'bills' }"
              @click="selectService('bills')"
          >
            <div class="service-icon bills">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <h3 class="service-title">County Bills</h3>
            <p class="service-desc">Pay miscellaneous bills and food handler certificates</p>
            <div class="service-actions" v-if="activeService === 'bills'">
              <button class="action-btn" @click.stop="goTo('pay-bill')">
                <span>Pay a Bill</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Property Services -->
          <div
              class="service-card"
              :class="{ 'active': activeService === 'property' }"
              @click="selectService('property')"
          >
            <div class="service-icon property">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <h3 class="service-title">Property Services</h3>
            <p class="service-desc">Update property info and access valuation roll</p>
            <div class="service-actions" v-if="activeService === 'property'">
              <button class="action-btn" @click.stop="goTo('property-self-service')">
                <span>Update Info</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
              <button class="action-btn" @click.stop="goTo('property-valuation')">
                <span>Valuation Roll</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section id="how-it-works" class="how-section">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">Simple Process</span>
          <h2 class="section-title">How It Works</h2>
          <p class="section-subtitle">Complete your payment in three easy steps</p>
        </div>

        <div class="steps-container">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>Select Service</h3>
              <p>Choose the county service you want to pay for from our available options</p>
            </div>
          </div>
          <div class="step-connector"></div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>Enter Details</h3>
              <p>Provide your reference number, ID, or other required information</p>
            </div>
          </div>
          <div class="step-connector"></div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>Pay & Download</h3>
              <p>Complete payment via M-Pesa and download your receipt instantly</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="contact-section">
      <div class="container">
        <div class="contact-grid">
          <div class="contact-info">
            <h2>Need Assistance?</h2>
            <p>Our support team is available to help you with any questions about county services and payments.</p>
            <div class="contact-details">
              <div class="contact-item" v-if="county.contact">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>{{ county.contact }}</span>
              </div>
              <div class="contact-item" v-if="county.email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>{{ county.email }}</span>
              </div>
              <div class="contact-item" v-if="county.address">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span>{{ county.address }}</span>
              </div>
            </div>
          </div>
          <div class="payment-methods">
            <h3>Accepted Payment Methods</h3>
            <div class="methods-list">
              <div class="method">
                <div class="method-icon mpesa"></div>
                <span>M-Pesa</span>
              </div>
              <div class="method" v-if="county.bank">
                <div class="method-icon bank"></div>
                <span>{{ county.bank }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Component -->
    <FooterComponent />

    <!-- Floating Chat Button -->
    <div class="floating-chat">
      <transition name="chat-widget">
        <div v-if="chatOpen" class="chat-widget">
          <!-- Chat Header -->
          <div class="chat-widget-header">
            <div class="chat-header-info">
              <div class="chat-avatar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                  <circle cx="7.5" cy="14.5" r="1.5"></circle>
                  <circle cx="16.5" cy="14.5" r="1.5"></circle>
                </svg>
              </div>
              <div>
                <h4>County Assistant</h4>
                <span class="online-status">
                  <span class="status-dot"></span>
                  Online
                </span>
              </div>
            </div>
            <div class="chat-header-actions">
              <button class="header-btn" @click="clearChat" title="Clear chat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
              <button class="header-btn" @click="goTo('live-chat')" title="Open full chat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </button>
              <button class="header-btn" @click="toggleChat" title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>

          <!-- Chat Messages -->
          <div class="chat-widget-messages" ref="chatMessages">
            <!-- Welcome Message -->
            <div v-if="chatMessages.length === 0" class="chat-welcome">
              <div class="welcome-avatar">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                  <circle cx="7.5" cy="14.5" r="1.5"></circle>
                  <circle cx="16.5" cy="14.5" r="1.5"></circle>
                </svg>
              </div>
              <h5>Hi there! 👋</h5>
              <p>I'm your County Assistant. How can I help you today?</p>
              <div class="quick-replies">
                <button
                    v-for="(q, i) in quickQuestions"
                    :key="i"
                    class="quick-reply"
                    @click="sendQuickQuestion(q)"
                >
                  {{ q }}
                </button>
              </div>
            </div>

            <!-- Messages -->
            <div
                v-for="(msg, index) in chatMessages"
                :key="index"
                class="chat-message"
                :class="msg.role"
            >
              <div class="message-bubble">
                {{ msg.content }}
              </div>
              <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
            </div>

            <!-- Typing Indicator -->
            <div v-if="chatLoading" class="chat-message assistant">
              <div class="message-bubble typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="chat-widget-input">
            <input
                v-model="chatInput"
                type="text"
                placeholder="Type a message..."
                @keyup.enter="sendChatMessage"
                :disabled="chatLoading"
                ref="chatInputField"
            />
            <button
                class="send-btn"
                @click="sendChatMessage"
                :disabled="!chatInput.trim() || chatLoading"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      </transition>

      <!-- Chat Toggle Button -->
      <button
          class="chat-toggle-btn"
          :class="{ 'chat-open': chatOpen }"
          @click="toggleChat"
      >
        <svg v-if="!chatOpen" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        <span class="chat-badge" v-if="!chatOpen && unreadCount > 0">{{ unreadCount }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { authUrl, biller, execute } from "@/api";

export default {
  name: "HomePage",
  components: {
    HeaderComponent,
    FooterComponent
  },
  data() {
    return {
      county: {
        id: null,
        countyID: null,
        countyName: null,
        tagline: null,
        contact: null,
        address: null,
        email: null,
        logo: null,
        bank: null,
        powered: null,
        handPhone: null
      },
      activeService: null,

      // Chat Widget with Conversation Context
      chatOpen: false,
      chatInput: '',
      chatMessages: [],      // Array of {role, content, timestamp, context}
      chatLoading: false,
      unreadCount: 0,
      chatContext: null,     // Store context from last response for follow-ups
      quickQuestions: [
        'How do I pay my bill?',
        'Business permit info',
        'Check my land rates',
        'Check payment status'
      ]
    };
  },
  mounted() {
    this.getCounty();
  },
  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (error) {
        console.error('Error fetching county data:', error);
      }
    },

    formatCountyName(name) {
      if (!name) return '';
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },

    goTo(route) {
      this.$router.push(route);
    },

    selectService(service) {
      this.activeService = this.activeService === service ? null : service;
    },

    // ==========================================
    // CHAT METHODS WITH CONVERSATION CONTEXT
    // ==========================================

    toggleChat() {
      this.chatOpen = !this.chatOpen;
      if (this.chatOpen) {
        this.unreadCount = 0;
        this.$nextTick(() => {
          this.$refs.chatInputField?.focus();
        });
      }
    },

    /**
     * Send chat message with conversation history for context
     */
    async sendChatMessage() {
      if (!this.chatInput.trim() || this.chatLoading) return;

      const userMessage = {
        role: 'user',
        content: this.chatInput,
        timestamp: new Date(),
        context: null
      };

      this.chatMessages.push(userMessage);
      const query = this.chatInput;
      this.chatInput = '';
      this.chatLoading = true;
      this.scrollChatToBottom();

      try {
        // Build conversation history for context-aware responses
        const conversationHistory = this.buildConversationHistory();

        const response = await this.queryAI(query, conversationHistory);

        const assistantMessage = {
          role: 'assistant',
          content: response.answer || 'I apologize, but I couldn\'t process that. Please try again.',
          timestamp: new Date(),
          context: response.context || null
        };

        this.chatMessages.push(assistantMessage);

        // Store context for next query (contains LR numbers, business IDs, etc.)
        if (response.context) {
          this.chatContext = response.context;
        }
      } catch (error) {
        this.chatMessages.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
          context: null
        });
        console.error('Chat error:', error);
      } finally {
        this.chatLoading = false;
        this.scrollChatToBottom();
      }
    },

    /**
     * Build conversation history array to send to backend
     * This enables follow-up questions like "when was it registered?"
     */
    buildConversationHistory() {
      // Get last 10 messages (5 exchanges) for context
      const recentMessages = this.chatMessages.slice(-10);

      return recentMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
    },

    /**
     * Query AI with conversation history for context-aware responses
     */
    async queryAI(query, conversationHistory = []) {
      const data = new FormData();
      data.append("function", "queryAIAssistant");
      data.append("query", query);
      data.append("sessionNames", sessionStorage.getItem("sessionNames") || '');
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo") || '');

      // Send conversation history as JSON for follow-up context
      data.append("conversation_history", JSON.stringify(conversationHistory));

      // Send previous context (contains LR numbers, business IDs from last query)
      if (this.chatContext) {
        data.append("previous_context", JSON.stringify(this.chatContext));
      }

      const response = await execute(data, biller);
      const aiResponse = response.data;

      if (aiResponse.success && aiResponse.data) {
        const aiData = aiResponse.data;
        return {
          success: true,
          answer: aiData.data?.answer || aiData.answer || 'No response received',
          tableData: aiData.data?.tableData || aiData.tableData || null,
          context: aiData.data?.context || aiData.context || null
        };
      }
      throw new Error(aiResponse.error || 'Failed to get response');
    },

    /**
     * Send a quick question from the preset options
     */
    sendQuickQuestion(question) {
      this.chatInput = question;
      this.sendChatMessage();
    },

    /**
     * Clear chat history and context - starts fresh conversation
     */
    clearChat() {
      this.chatMessages = [];
      this.chatContext = null;
    },

    /**
     * Scroll chat container to bottom after new message
     */
    scrollChatToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    /**
     * Format timestamp for display
     */
    formatTime(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /**
     * Format column name for table display
     */
    formatColumnName(name) {
      return name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

:root {
  --color-primary: #0f4c81;
  --color-primary-dark: #0a3459;
  --color-primary-light: #1a6bb5;
  --color-accent: #00a86b;
  --color-accent-light: #e6f7f1;
  --color-text: #1e293b;
  --color-text-muted: #64748b;
  --color-bg: #ffffff;
  --color-bg-subtle: #f8fafc;
  --color-border: #e2e8f0;
  --font-display: 'Plus Jakarta Sans', sans-serif;
  --font-body: 'DM Sans', sans-serif;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --radius-xl: 24px;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body { font-family: var(--font-body); color: var(--color-text); line-height: 1.6; background: var(--color-bg); -webkit-font-smoothing: antialiased; }
.portal { overflow-x: hidden; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.hero { position: relative; padding: 140px 0 100px; overflow: hidden; }
.hero-bg { position: absolute; inset: 0; background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); z-index: -2; }
.hero-bg::before { content: ''; position: absolute; inset: 0; background-image: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.05) 0%, transparent 40%); }
.hero-content { max-width: 1200px; margin: 0 auto; padding: 0 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
.hero-text { color: white; }
.hero-badge { display: inline-block; padding: 8px 16px; background: rgba(255, 255, 255, 0.15); border-radius: 100px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 24px; }
.hero-title { font-family: var(--font-display); font-size: clamp(36px, 5vw, 56px); font-weight: 800; line-height: 1.1; margin-bottom: 20px; }
.hero-title .highlight { color: #7dd3fc; }
.hero-subtitle { font-size: 18px; opacity: 0.9; max-width: 480px; margin-bottom: 40px; line-height: 1.7; }
.hero-stats { display: flex; align-items: center; gap: 24px; padding: 24px 32px; background: rgba(255, 255, 255, 0.1); border-radius: var(--radius-lg); backdrop-filter: blur(10px); width: fit-content; }
.stat { text-align: center; }
.stat-number { display: block; font-family: var(--font-display); font-size: 28px; font-weight: 800; }
.stat-label { font-size: 13px; opacity: 0.8; }
.stat-divider { width: 1px; height: 40px; background: rgba(255, 255, 255, 0.2); }
.hero-visual { display: flex; justify-content: center; }
.hero-phone { max-width: 320px; width: 100%; height: auto; filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3)); animation: float 6s ease-in-out infinite; }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
.hero-wave { position: absolute; bottom: 0; left: 0; right: 0; z-index: 1; }
.hero-wave svg { display: block; width: 100%; height: auto; }

/* Services */
.services-section { padding: 80px 0 100px; background: var(--color-bg-subtle); }
.section-header { text-align: center; margin-bottom: 60px; }
.section-badge { display: inline-block; padding: 6px 14px; background: var(--color-accent-light); color: var(--color-accent); border-radius: 100px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 16px; }
.section-title { font-family: var(--font-display); font-size: clamp(28px, 4vw, 40px); font-weight: 800; color: var(--color-text); margin-bottom: 12px; }
.section-subtitle { font-size: 16px; color: var(--color-text-muted); }
.services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.service-card { background: white; border-radius: var(--radius-lg); padding: 32px; border: 2px solid transparent; transition: all 0.3s ease; cursor: pointer; }
.service-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.service-card.active { border-color: var(--color-primary); box-shadow: var(--shadow-xl); }
.service-icon { width: 64px; height: 64px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
.service-icon.business { background: #fef3c7; color: #d97706; }
.service-icon.land { background: #dbeafe; color: #2563eb; }
.service-icon.rents { background: #fce7f3; color: #db2777; }
.service-icon.parking { background: #d1fae5; color: #059669; }
.service-icon.bills { background: #e0e7ff; color: #4f46e5; }
.service-icon.property { background: #fee2e2; color: #dc2626; }
.service-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--color-text); margin-bottom: 8px; }
.service-desc { font-size: 14px; color: var(--color-text-muted); line-height: 1.5; }
.service-actions { display: flex; flex-direction: column; gap: 8px; margin-top: 20px; padding-top: 20px; border-top: 1px solid var(--color-border); animation: slideDown 0.3s ease; }
@keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
.action-btn { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: var(--color-bg-subtle); border: none; border-radius: var(--radius-sm); font-family: var(--font-body); font-size: 14px; font-weight: 500; color: var(--color-text); cursor: pointer; transition: all 0.2s; }
.action-btn:hover { background: var(--color-primary); color: white; }
.action-btn svg { opacity: 0; transform: translateX(-8px); transition: all 0.2s; }
.action-btn:hover svg { opacity: 1; transform: translateX(0); }

/* How It Works */
.how-section { padding: 80px 0 100px; background: white; }
.steps-container { display: flex; align-items: flex-start; justify-content: center; gap: 0; }
.step { flex: 1; max-width: 280px; text-align: center; padding: 0 24px; }
.step-number { width: 56px; height: 56px; background: var(--color-primary); color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: var(--font-display); font-size: 24px; font-weight: 700; margin: 0 auto 20px; }
.step-content h3 { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--color-text); margin-bottom: 8px; }
.step-content p { font-size: 14px; color: var(--color-text-muted); line-height: 1.6; }
.step-connector { width: 80px; height: 2px; background: var(--color-border); margin-top: 28px; flex-shrink: 0; }

/* Contact */
.contact-section { padding: 80px 0; background: var(--color-bg-subtle); }
.contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: start; }
.contact-info h2 { font-family: var(--font-display); font-size: 28px; font-weight: 700; margin-bottom: 16px; }
.contact-info > p { color: var(--color-text-muted); margin-bottom: 32px; line-height: 1.7; }
.contact-details { display: flex; flex-direction: column; gap: 16px; }
.contact-item { display: flex; align-items: center; gap: 12px; color: var(--color-text); }
.contact-item svg { color: var(--color-primary); flex-shrink: 0; }
.payment-methods h3 { font-family: var(--font-display); font-size: 18px; font-weight: 700; margin-bottom: 20px; }
.methods-list { display: flex; gap: 16px; }
.method { display: flex; align-items: center; gap: 12px; padding: 16px 24px; background: white; border-radius: var(--radius-md); box-shadow: var(--shadow-sm); }
.method-icon { width: 40px; height: 40px; border-radius: var(--radius-sm); background-size: contain; background-repeat: no-repeat; background-position: center; }
.method-icon.mpesa { background-color: #00a651; }
.method-icon.bank { background-color: var(--color-primary); }

/* ========== FLOATING CHAT WIDGET ========== */
.floating-chat { position: fixed; bottom: 24px; right: 24px; z-index: 9999; font-family: var(--font-body); }

.chat-toggle-btn { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border: none; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 20px rgba(15, 76, 129, 0.4); transition: all 0.3s ease; position: relative; }
.chat-toggle-btn:hover { transform: scale(1.08); box-shadow: 0 6px 25px rgba(15, 76, 129, 0.5); }
.chat-toggle-btn.chat-open { background: #64748b; }
.chat-badge { position: absolute; top: -4px; right: -4px; width: 22px; height: 22px; background: #ef4444; color: white; font-size: 12px; font-weight: 700; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; }

.chat-widget { position: absolute; bottom: 80px; right: 0; width: 380px; height: 520px; background: white; border-radius: 20px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2); display: flex; flex-direction: column; overflow: hidden; }

.chat-widget-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; }
.chat-header-info { display: flex; align-items: center; gap: 12px; }
.chat-avatar { width: 44px; height: 44px; background: rgba(255, 255, 255, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.chat-header-info h4 { font-size: 15px; font-weight: 700; margin: 0 0 2px; }
.online-status { display: flex; align-items: center; gap: 6px; font-size: 12px; opacity: 0.9; }
.status-dot { width: 8px; height: 8px; background: #10b981; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.chat-header-actions { display: flex; gap: 8px; }
.header-btn { width: 36px; height: 36px; background: rgba(255, 255, 255, 0.15); border: none; border-radius: 10px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
.header-btn:hover { background: rgba(255, 255, 255, 0.25); }

.chat-widget-messages { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 12px; background: #f8fafc; }

.chat-welcome { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 20px 10px; }
.welcome-avatar { width: 70px; height: 70px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #2563eb; margin-bottom: 16px; }
.chat-welcome h5 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.chat-welcome p { font-size: 14px; color: #64748b; margin: 0 0 20px; line-height: 1.5; }
.quick-replies { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.quick-reply { padding: 8px 14px; background: white; border: 1px solid #e2e8f0; border-radius: 20px; font-size: 13px; color: #475569; cursor: pointer; transition: all 0.2s; }
.quick-reply:hover { background: #0f4c81; border-color: #0f4c81; color: white; }

.chat-message { display: flex; flex-direction: column; max-width: 85%; }
.chat-message.user { align-self: flex-end; }
.chat-message.assistant { align-self: flex-start; }
.message-bubble { padding: 12px 16px; border-radius: 16px; font-size: 14px; line-height: 1.5; }
.chat-message.user .message-bubble { background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); color: white; border-bottom-right-radius: 4px; }
.chat-message.assistant .message-bubble { background: white; color: #1e293b; border-bottom-left-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.message-time { font-size: 11px; color: #94a3b8; margin-top: 4px; padding: 0 4px; }
.chat-message.user .message-time { text-align: right; }

.message-bubble.typing { display: flex; gap: 4px; padding: 16px 20px; }
.message-bubble.typing span { width: 8px; height: 8px; background: #94a3b8; border-radius: 50%; animation: typingBounce 1.4s infinite; }
.message-bubble.typing span:nth-child(2) { animation-delay: 0.2s; }
.message-bubble.typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typingBounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }

.chat-widget-input { display: flex; gap: 10px; padding: 16px 20px; background: white; border-top: 1px solid #e2e8f0; }
.chat-widget-input input { flex: 1; padding: 12px 16px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 14px; font-family: inherit; transition: border-color 0.2s; }
.chat-widget-input input:focus { outline: none; border-color: #0f4c81; }
.chat-widget-input input::placeholder { color: #94a3b8; }
.chat-widget-input .send-btn { width: 48px; height: 48px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border: none; border-radius: 12px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
.chat-widget-input .send-btn:hover:not(:disabled) { transform: scale(1.05); }
.chat-widget-input .send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.chat-widget-enter-active, .chat-widget-leave-active { transition: all 0.3s ease; }
.chat-widget-enter-from, .chat-widget-leave-to { opacity: 0; transform: translateY(20px) scale(0.95); }

/* Responsive */
@media (max-width: 1024px) {
  .services-grid { grid-template-columns: repeat(2, 1fr); }
  .hero-content { grid-template-columns: 1fr; text-align: center; }
  .hero-subtitle { margin-left: auto; margin-right: auto; }
  .hero-stats { margin: 0 auto; }
  .hero-visual { display: none; }
}

@media (max-width: 768px) {
  .services-grid { grid-template-columns: 1fr; }
  .steps-container { flex-direction: column; gap: 32px; }
  .step-connector { width: 2px; height: 40px; margin: 0 auto; }
  .contact-grid { grid-template-columns: 1fr; gap: 40px; }
  .hero { padding: 120px 0 80px; }
  .hero-stats { flex-direction: column; gap: 16px; width: 100%; max-width: 280px; }
  .stat-divider { width: 60px; height: 1px; }
  .chat-widget { width: calc(100vw - 32px); height: calc(100vh - 120px); bottom: 80px; right: -8px; max-height: 600px; }
  .floating-chat { bottom: 16px; right: 16px; }
  .chat-toggle-btn { width: 56px; height: 56px; }
}

@media (max-width: 480px) {
  .container { padding: 0 16px; }
  .service-card { padding: 24px; }
  .methods-list { flex-direction: column; }
  .chat-widget { width: calc(100vw - 24px); right: -12px; }
}
</style>